/**
 * storage/tokenStore.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Where the access token lives — and the one place the three platforms differ.
 *
 * WEB      The platform issues httpOnly cookies. JavaScript cannot read them,
 *          which is the point: an XSS on this origin cannot exfiltrate the
 *          session. Nothing is stored here; axios sends the cookie itself via
 *          `withCredentials`, and every function below is a no-op returning
 *          null. Do not "fix" that by mirroring the token into localStorage —
 *          it would throw away the httpOnly guarantee for no benefit.
 *
 * NATIVE   There is no cookie jar shared with a browser, so the platform app
 *          hands this app a token over the deep link and it is persisted here.
 *          The request interceptor then sends it as `Authorization: Bearer …`,
 *          which sk-company-cash-api's authenticate() accepts equally
 *          (middleware/auth.ts extractToken).
 *
 * An in-memory copy backs the async store so the request interceptor — which
 * cannot await — always has the current token synchronously.
 * ──────────────────────────────────────────────────────────────────────────
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const ACCESS_KEY = 'sk_company_cash.access_token';
const REFRESH_KEY = 'sk_company_cash.refresh_token';

const usesCookies = Platform.OS === 'web';

/** Synchronous mirror, kept in step with every write below. */
let accessMemo: string | null = null;
let refreshMemo: string | null = null;

/** Read the access token without awaiting. Always null on web. */
export const peekAccessToken = (): string | null => (usesCookies ? null : accessMemo);

export const peekRefreshToken = (): string | null => (usesCookies ? null : refreshMemo);

/** Hydrate the memo from disk. Called once, during app boot. */
export async function loadTokens(): Promise<void> {
  if (usesCookies) return;
  const stored = await AsyncStorage.getMany([ACCESS_KEY, REFRESH_KEY]);
  accessMemo = stored[ACCESS_KEY] ?? null;
  refreshMemo = stored[REFRESH_KEY] ?? null;
}

/** Persist a token pair received from the platform hand-off. */
export async function saveTokens(access: string, refresh?: string | null): Promise<void> {
  if (usesCookies) return;
  accessMemo = access;
  const writes: Record<string, string> = { [ACCESS_KEY]: access };
  if (refresh) {
    refreshMemo = refresh;
    writes[REFRESH_KEY] = refresh;
  }
  await AsyncStorage.setMany(writes);
}

/** Replace only the access token — what a silent refresh produces. */
export async function saveAccessToken(access: string): Promise<void> {
  if (usesCookies) return;
  accessMemo = access;
  await AsyncStorage.setItem(ACCESS_KEY, access);
}

/** Forget the session. Called on sign-out and on a terminal 401. */
export async function clearTokens(): Promise<void> {
  accessMemo = null;
  refreshMemo = null;
  if (usesCookies) return;
  await AsyncStorage.removeMany([ACCESS_KEY, REFRESH_KEY]);
}

/** True when this platform relies on the platform's httpOnly cookies. */
export const isCookieSession = (): boolean => usesCookies;
